import { useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { useAddressAutoComplete } from "../../state/addressAutoCompleteState";
import { useAddress } from "../../state/addressState";
import { useDebounce } from "../../hooks/useDebounce";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";
// MUI
// import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

export default function ReturnAddressForm({ closeDialog, setValue }) {
  const [searchAddress, setSearchAddress] = useState("");
  const [searchAddressID, setSearchAddressID] = useState("");
  const debouncedValue = useDebounce(searchAddress, 1000);
  const [addressOptions, setAddressOptions] = useState([]);
  const { setAlertNotification } = useContext(AlertNotificationContext);

  // Form
  let defaultValues = {
    name: "",
    address: "",
    address2: "",
    county: "",
    city: "",
    postcode: "",
    country: "GB",
    residential: false,
    contact: "",
    contactEmail: "",
    contactMobile: "",
  };
  const {
    handleSubmit: handleAddressSubmit,
    reset: resetAddress,
    control: controlAddress,
    setValue: setValueAddress,
    watch: watchAddress,
  } = useForm({
    defaultValues,
  });
  const watchResidential = watchAddress("residential", false);

  //  Call get address by ID
  const { data: addressData, isSuccess: addressSuccess } = useAddress(
    {
      params: {
        id: searchAddressID,
      },
    },
    searchAddressID ? true : false,
  );

  // Success Function
  const addressSuccessFunc = () => {
    setValueAddress("address", addressData.data.line_1);
    setValueAddress("address2", addressData.data.line_2);
    setValueAddress("county", addressData.data.county);
    setValueAddress("city", addressData.data.town_or_city);
    setValueAddress("postcode", addressData.data.postcode);
    setValueAddress("country", changeCountryFormat(addressData.data.country));
    setValueAddress("residential", !!addressData.data.residential);
  };

  // Error Function
  const addressErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${addressData.status}: ${addressData.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    addressSuccess,
    addressData,
    addressData?.status,
    addressSuccessFunc,
    addressErrorFunc,
  );

  // Call autocomplete
  const { data: autoCompleteData, isSuccess: autocompleteSuccess } =
    useAddressAutoComplete(
      {
        params: {
          address: debouncedValue,
        },
      },
      debouncedValue ? true : false,
    );

  // Success Function
  const autocompleteSuccessFunc = () => {
    setAddressOptions(autoCompleteData.data);
  };

  // Error Function
  const autocompleteErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${autoCompleteData.status}: ${autoCompleteData.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    autocompleteSuccess,
    autoCompleteData,
    autoCompleteData?.status,
    autocompleteSuccessFunc,
    autocompleteErrorFunc,
  );

  const onInputChange = (e) => {
    const address = e.target.value;
    if (address) {
      setSearchAddress(address);
    } else {
      setAddressOptions([]);
    }
  };

  const handleAddressSearch = (event, value) => {
    setSearchAddressID(value.id);
  };

  const validationHelper = {
    name: { required: "Name is required" },
    address: { required: "Address is required" },
    address2: { required: "Address 2 is required" },
    county: { required: "County is required" },
    city: { required: "City is required" },
    postcode: { required: "Postcode is required" },
    country: { required: "Country is required" },
    contact: { required: "Contact is required" },
    contactEmail: {
      required: "Contact Email is required",
      pattern: "Invalid email address",
    },
    contactMobile: { required: "Contact Mobile is required" },
  };

  // Convert country returned by API to country code compatible with BC
  const changeCountryFormat = (country) => {
    if (
      country === "England" ||
      country === "Wales" ||
      country === "Scotland" ||
      country === "Northern Ireland"
    ) {
      return "GB";
    } else {
      return "";
    }
  };

  const handleOnSubmit = (data) => {
    setValue("returnOrder.name", data.name);
    setValue("returnOrder.address", data.address);
    setValue("returnOrder.address2", data.address2);
    setValue("returnOrder.city", data.city);
    setValue("returnOrder.county", data.county);
    setValue("returnOrder.postCode", data.postcode);
    setValue("returnOrder.country", data.country);
    setValue("returnOrder.homeaddress", !!data.residential);
    setValue("returnOrder.contact", data.contact);
    setValue("returnOrder.email", data.contactEmail);
    setValue("returnOrder.mobileNo", data.contactMobile);
    closeDialog();
  };

  const NameInput = () => {
    return (
      <Controller
        name="name"
        control={controlAddress}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Name"
            error={error !== undefined}
            helperText={error ? validationHelper.name[error.type] : ""}
          />
        )}
      />
    );
  };

  const Address1Input = () => {
    return (
      <Controller
        name="address"
        control={controlAddress}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Address"
            error={error !== undefined}
            helperText={error ? validationHelper.address[error.type] : ""}
          />
        )}
      />
    );
  };

  const Address2Input = () => {
    return (
      <Controller
        name="address2"
        control={controlAddress}
        rules={{
          required: false,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Address 2"
            error={error !== undefined}
            helperText={error ? validationHelper.address2[error.type] : ""}
          />
        )}
      />
    );
  };

  const CountyInput = () => {
    return (
      <Controller
        name="county"
        control={controlAddress}
        rules={{
          required: false,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="County"
            error={error !== undefined}
            helperText={error ? validationHelper.county[error.type] : ""}
          />
        )}
      />
    );
  };

  const CityInput = () => {
    return (
      <Controller
        name="city"
        control={controlAddress}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="City"
            error={error !== undefined}
            helperText={error ? validationHelper.city[error.type] : ""}
          />
        )}
      />
    );
  };

  const PostcodeInput = () => {
    return (
      <Controller
        name="postcode"
        control={controlAddress}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Postcode"
            error={error !== undefined}
            helperText={error ? validationHelper.postcode[error.type] : ""}
          />
        )}
      />
    );
  };

  const HomeInput = () => {
    return (
      <Controller
        name="residential"
        control={controlAddress}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            control={<Switch checked={value} onChange={onChange} />}
            label="Residential Address"
          />
        )}
      />
    );
  };

  const ContactInput = () => {
    return (
      <Controller
        name="contact"
        control={controlAddress}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Contact"
            error={error !== undefined}
            helperText={error ? validationHelper.contact[error.type] : ""}
          />
        )}
      />
    );
  };

  const ContactMobileInput = () => {
    return (
      <Controller
        name="contactMobile"
        control={controlAddress}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Contact Mobile"
            error={error !== undefined}
            helperText={error ? validationHelper.contactMobile[error.type] : ""}
          />
        )}
      />
    );
  };

  const ContactEmailInput = () => {
    return (
      <Controller
        name="contactEmail"
        control={controlAddress}
        rules={{
          required: true,
          pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Contact Email"
            error={error !== undefined}
            helperText={error ? validationHelper.contactEmail[error.type] : ""}
          />
        )}
      />
    );
  };

  const countryArray = ["", "GB", "IE"];
  const CountryInput = ({ countryArray }) => {
    return (
      <Controller
        name="country"
        control={controlAddress}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth error={error ? true : false}>
            <InputLabel id="country-label">Country</InputLabel>
            <Select
              labelId="country-label"
              {...field}
              label="Country"
              error={error !== undefined}
            >
              {countryArray.map((country) => {
                return (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                );
              })}
            </Select>
            {error ? (
              <FormHelperText>
                {validationHelper.country[error.type]}
              </FormHelperText>
            ) : null}
          </FormControl>
        )}
      />
    );
  };

  return (
    <Grid
      autoComplete="off"
      container
      spacing={2}
      sx={{
        p: 2,
      }}
    >
      <Grid size={12}>
        <Autocomplete
          onInputChange={onInputChange}
          onChange={handleAddressSearch}
          filterOptions={(x) => x}
          freeSolo
          id="search-address"
          disableClearable
          options={addressOptions}
          getOptionLabel={(option) => option.address || ""}
          getOptionKey={(option) => option.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search address.."
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  type: "search",
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <NameInput />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Address1Input />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Address2Input />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <CountyInput />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <CityInput />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <PostcodeInput />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <CountryInput countryArray={countryArray} />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <HomeInput />
      </Grid>
      {watchResidential && (
        <>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ContactInput />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ContactEmailInput />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ContactMobileInput />
          </Grid>
        </>
      )}
      <Grid size={12}>
        <Stack
          direction="row-reverse"
          spacing={2}
          sx={{
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            onClick={handleAddressSubmit(handleOnSubmit)}
          >
            Save
          </Button>
          <Button onClick={() => resetAddress()} variant="outlined">
            Clear
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}
